import dayjs from '@lib/dayjs';

/**
 * Class for formatting values.
 * @class Formatter
 */
class Formatter {
  /**
   * 기본 숫자 포맷팅
   * @param number
   * @returns
   */
  formatNumber(num: string | number): string {
    return formatWithFallback(num, (value) =>
      !Number.isNaN(value) ? Number(value).toLocaleString() : '-',
    );
  }

  /**
   * 날짜 포맷팅
   * @param date
   * @returns
   */
  formatDate(date: Date | string): string {
    return formatWithFallback(date, (value) => {
      const date = new Date(value);
      return `${date.getFullYear()}-${padDate(date.getMonth() + 1)}-${padDate(date.getDate())}`;
    });
  }

  /**
   * 날짜 및 시간 포맷팅
   * @param date
   * @returns
   */
  formatDateTime(date: string): string {
    return formatWithFallback(date, (value) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) throw new Error('Invalid date');
      return `${date.getFullYear()}-${padDate(date.getMonth() + 1)}-${padDate(
        date.getDate(),
      )} ${padDate(date.getHours())}:${padDate(date.getMinutes())}:${padDate(date.getSeconds())}`;
    });
  }

  /**
   * API 조회용 날짜 범위 포맷팅 (시작일: 00:00:00, 종료일: 23:59:59)
   * @param startDate 시작 날짜
   * @param endDate 종료 날짜
   * @returns { start: string, end: string } 형식의 객체
   */
  formatDateRange(
    startDate: Date | string,
    endDate: Date | string,
  ): { start: string; end: string } {
    const start = formatWithFallback(startDate, (value) =>
      dayjs(value).format('YYYY-MM-DD 00:00:00'),
    );
    const end = formatWithFallback(endDate, (value) => dayjs(value).format('YYYY-MM-DD 23:59:59'));
    return { start, end };
  }

  /**
   * 타이머 포맷팅 (MM:ss)
   * @param num
   * @returns
   */
  formatSecondsToMMss(num?: number): string {
    return formatWithFallback(num, secondsToMMss);
  }

  /**
   * 퍼센테이지 포맷팅
   * @param num
   * @returns
   */
  formatPercentage(num: string | number): string {
    return formatWithFallback(num, (value) => this.formatNumber(value) + '%');
  }

  /**
   * 포인트 정보 포맷팅
   * @param num
   * @returns
   */
  formatPoint(num: string | number): string {
    return formatWithFallback(num, (value) => this.formatNumber(value) + 'pt');
  }

  /**
   * 전화번호 포맷팅
   * @param phone
   * @returns
   */
  formatPhone(phone: string): string {
    return formatWithFallback(phone, addHyphenToPhoneNumber);
  }

  /**
   * 사업자 등록번호 포맷팅
   * @param registrationNumber
   * @returns
   */
  formatRegistrationNumber(registrationNumber: string): string {
    return formatWithFallback(registrationNumber, addHyphenToRegistrationNumber);
  }

  /**
   * 초를 시:분:초 형태로 변환
   * @param seconds 초 (양수 또는 음수)
   * @returns HH:MM:SS 형식 문자열 (음수일 경우 -HH:MM:SS)
   */
  formatSecondsToHHmmss(seconds: number): string {
    return formatWithFallback(seconds, (value) => {
      const absSeconds = Math.abs(value);
      const h = Math.floor(absSeconds / 3600);
      const m = Math.floor((absSeconds % 3600) / 60);
      const s = Math.floor(absSeconds % 60);
      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s
        .toString()
        .padStart(2, '0')}`;
      return value < 0 ? `-${timeStr}` : timeStr;
    });
  }

  /**
   * 초를 시:분:초 형태로 변환 (음수일 경우 ' 초과' 텍스트 추가)
   * @param seconds 초 (양수 또는 음수)
   * @returns HH:MM:SS 형식 문자열 (음수일 경우 'HH:MM:SS 초과')
   */
  formatSecondsWithExcess(seconds: number): string {
    return formatWithFallback(seconds, (value) => {
      const absSeconds = Math.abs(value);
      const h = Math.floor(absSeconds / 3600);
      const m = Math.floor((absSeconds % 3600) / 60);
      const s = Math.floor(absSeconds % 60);
      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s
        .toString()
        .padStart(2, '0')}`;
      return value < 0 ? `${timeStr} 초과` : timeStr;
    });
  }

  /**
   * 초를 한글 시간 형태로 변환 (시간/분만 표시)
   * @param seconds 초
   * @returns 한글 시간 문자열 (예: "1시간 30분", "45분")
   */
  formatSecondsToKoreanTime(seconds: number): string {
    return formatWithFallback(seconds, (value) => {
      const h = Math.floor(value / 3600);
      const m = Math.floor((value % 3600) / 60);
      return h > 0 ? (m > 0 ? `${h}시간 ${m}분` : `${h}시간`) : `${m}분`;
    });
  }

  /**
   * 시간 문자열 포맷팅 (HHmm → HH:mm)
   * @param time HHmm 형식의 시간 문자열 (예: "0900", "1430")
   * @returns HH:mm 형식 문자열 (예: "09:00", "14:30")
   */
  formatTimeString(time: string): string {
    return formatWithFallback(time, (value) => {
      if (!value || value.length < 4) throw new Error('Invalid time string');
      return `${value.slice(0, 2)}:${value.slice(2, 4)}`;
    });
  }

  /**
   * 이용권 남은 시간 계산
   * @param ticket 이용권 정보
   * @returns 남은 시간 문자열
   */
  calculateTicketRemainTime(ticket: Ticket): string {
    try {
      switch (ticket.type) {
        case 1: // 일회권
          return this.calculateOneTimeTicketRemain(ticket);
        case 2: // 기간권
          return this.calculatePeriodTicketRemain(ticket);
        case 3: // 시간정액권
          return this.calculateTimePackageRemain(ticket);
        case 4: // 세미나실
          return this.calculateSeminarRemain(ticket);
        case 5: // 사물함
          return this.calculateLockerRemain(ticket);
        default:
          return this.formatSecondsToHHmmss(ticket.remainTime);
      }
    } catch (error) {
      return '-';
    }
  }

  /**
   * 일회권 남은 시간 계산
   */
  private calculateOneTimeTicketRemain(ticket: Ticket): string {
    const now = dayjs();

    // 시간 초과 상태
    if (ticket.isUsing === 2 && ticket.lastOut) {
      const excessSeconds = now.diff(dayjs(ticket.lastOut), 'second');
      return this.formatSecondsWithExcess(-Math.abs(excessSeconds));
    }

    // 사용 중 상태 (입장 O, 퇴장 X 또는 재입장)
    if (ticket.isUsing === 1 && ticket.lastIn) {
      const isReEntry = ticket.lastOut && dayjs(ticket.lastIn).isAfter(dayjs(ticket.lastOut));
      if (!ticket.lastOut || isReEntry) {
        const usedSeconds = now.diff(dayjs(ticket.lastIn), 'second');
        return this.formatSecondsToHHmmss(ticket.remainTime - usedSeconds);
      }
    }

    return this.formatSecondsToHHmmss(ticket.remainTime);
  }

  /**
   * 기간권 남은 시간 계산 (날짜 기반)
   */
  private calculatePeriodTicketRemain(ticket: Ticket): string {
    const now = dayjs();
    const daysRemaining = dayjs(ticket.endDate).startOf('day').diff(now.startOf('day'), 'day');

    if (daysRemaining < 0) return '만료';
    return `${daysRemaining}일 남음`;
  }

  /**
   * 시간정액권 남은 시간 계산 (시간 기반)
   */
  private calculateTimePackageRemain(ticket: Ticket): string {
    const now = dayjs();

    // 시간 초과 상태
    if (ticket.isUsing === 2 && ticket.lastOut) {
      const excessSeconds = now.diff(dayjs(ticket.lastOut), 'second');
      return this.formatSecondsWithExcess(-Math.abs(excessSeconds));
    }

    // 사용 중 상태 (입장 O, 퇴장 X 또는 재입장)
    if (ticket.isUsing === 1 && ticket.lastIn) {
      const isReEntry = ticket.lastOut && dayjs(ticket.lastIn).isAfter(dayjs(ticket.lastOut));
      if (!ticket.lastOut || isReEntry) {
        const usedSeconds = now.diff(dayjs(ticket.lastIn), 'second');
        return this.formatSecondsToHHmmss(ticket.remainTime - usedSeconds);
      }
    }

    return this.formatSecondsToHHmmss(ticket.remainTime);
  }

  /**
   * 세미나실 남은 시간 계산
   */
  private calculateSeminarRemain(ticket: Ticket): string {
    const now = dayjs();
    const startTime = dayjs(ticket.startDate);
    const endTime = dayjs(ticket.endDate);

    // 예약 시작 전: 전체 예약 시간 표시
    if (now.isBefore(startTime)) {
      const totalSeconds = endTime.diff(startTime, 'second');
      return this.formatSecondsToHHmmss(totalSeconds);
    }

    // 사용 중 (시작 ~ 종료 사이)
    if (now.isBefore(endTime)) {
      const remainSeconds = endTime.diff(now, 'second');
      return this.formatSecondsToHHmmss(remainSeconds);
    }

    // 종료됨
    return '00:00:00';
  }

  /**
   * 사물함 남은 시간 계산
   */
  private calculateLockerRemain(ticket: Ticket): string {
    const now = dayjs();
    const endTime = dayjs(ticket.endDate);
    const daysRemaining = endTime.startOf('day').diff(now.startOf('day'), 'day');

    if (daysRemaining < 0) return '만료';
    return `${daysRemaining}일 남음`;
  }
}

const Format = new Formatter();
export default Format;

/**
 * Format a value with a fallback value.
 * @param value value to format
 * @param formatFunction function for formatting
 * @returns formatted value or fallback value (on error)
 */
const formatWithFallback = (value: any, formatFunction: (value: any) => string) => {
  try {
    return formatFunction(value);
  } catch (error) {
    return '-';
  }
};

/**
 * 전화번호에 하이픈 붙이기 (지역번호 없는 전화번호도 포함)
 * @author @jhlov https://github.com/jhlov
 * @param value 전화번호
 * @returns 하이픈이 붙은 전화번호
 */
const addHyphenToPhoneNumber = (value: string) => {
  if (!value) {
    return '';
  }

  value = value.replace(/[^0-9]/g, '');

  let result = [];
  let restNumber = '';

  // 지역번호와 나머지 번호로 나누기
  if (value.startsWith('02')) {
    // 서울 02 지역번호
    result.push(value.substr(0, 2));
    restNumber = value.substring(2);
  } else if (value.startsWith('1')) {
    // 지역 번호가 없는 경우
    // 1xxx-yyyy
    restNumber = value;
  } else {
    // 나머지 3자리 지역번호
    // 0xx-yyyy-zzzz
    result.push(value.substr(0, 3));
    restNumber = value.substring(3);
  }

  if (restNumber.length === 7) {
    // 7자리만 남았을 때는 xxx-yyyy
    result.push(restNumber.substring(0, 3));
    result.push(restNumber.substring(3));
  } else {
    result.push(restNumber.substring(0, 4));
    result.push(restNumber.substring(4));
  }

  return result.filter((val) => val).join('-');
};

/**
 * 사업자 등록번호에 하이픈 추가
 * @param value 사업자 등록번호
 * @returns 하이픈이 붙은 사업자 등록번호
 */
const addHyphenToRegistrationNumber = (value: string) =>
  `${value.substring(0, 3)}-${value.substring(3, 5)}-${value.substring(5)}`;

const padDate = (value: number) => value.toString().padStart(2, '0');

const secondsToMMss = (counter: number) => {
  return `${Math.floor((counter || 0) / 60)
    .toString()
    .padStart(2, '0')}:${((counter || 0) % 60).toString().padStart(2, '0')}`;
};
